/**
 * Monster Onesie — TEMPORARY theme-side review renderer (BRIEF §5.7).
 *
 * ============================================================================
 * THIS FILE IS DISPOSABLE. It exists because Judge.me's rebuilt (v3) widget is
 * broken on this store: `jdgm.initializeWidgets()` throws
 * `TypeError: jdgm.setupMediaGallery is not a function` — the call site is in
 * their bundle, the definition is not — and their `_safeRun` swallows it, so
 * the widget renders nothing and the section goes blank. Evidence and the
 * message sent to their support are in JUDGEME-SUPPORT-NOTE.md.
 *
 * WHEN JUDGE.ME CONFIRMS THE FIX: set the section's "Reviews source" setting
 * back to "Judge.me widget" in the Theme Editor, check pagination and the
 * summary tile against the real widget, then delete this file, its <template>
 * markup and the .monster-reviews__* stylesheet block in one commit. The
 * jdgm-* CSS in sections/monster-reviews.liquid is deliberately untouched and
 * is what the widget branch still renders against.
 *
 * It reads the SAME public endpoint their own widget calls. No proxy, no app,
 * no server, no credentials — the endpoint answers a plain cross-origin GET
 * with `Access-Control-Allow-Origin: *`.
 * ============================================================================
 *
 * Reviews are user-generated content, so nothing from the payload is ever
 * treated as markup: every string lands via textContent, and image URLs are
 * accepted only over https from Judge.me's own image hosts.
 */

/** Judge.me caps per_page at 30 server-side regardless of what we ask for. */
const PER_PAGE = 5;

/** Only these hosts may supply a review photo, and only over https. */
const IMAGE_HOSTS = /(^|\.)(judgeme\.com|judge\.me)$/i;

/**
 * Judge.me already abbreviates display names to "First L." — verified against
 * the live payload, which returns "Maya R.", never "Maya Rodriguez". This runs
 * anyway so that a change on their side cannot silently start publishing full
 * surnames on our storefront. Anything already abbreviated passes through.
 *
 * @param {string} raw
 * @returns {string}
 */
function abbreviateName(raw) {
  const name = String(raw || '').trim();
  if (!name) return 'Anonymous';

  const parts = name.split(/\s+/);
  if (parts.length < 2) return name;

  const last = parts[parts.length - 1];
  // Already an initial ("R." / "R") — leave it alone.
  const initial = last.replace('.', '').length === 1 ? last.replace('.', '') : last[0];

  return parts.slice(0, -1).join(' ') + ' ' + initial.toUpperCase() + '.';
}

/**
 * Judge.me sends the review body as HTML (`<p>…</p>`). We want its TEXT.
 *
 * DOMParser builds an inert document — no scripts run, no resources load, and
 * it is never attached to this page — so this is a parse, not a render. The
 * result is assigned with textContent, so the review can never contribute
 * markup to our DOM. Block boundaries become newlines and survive as real line
 * breaks via `white-space: pre-line` in the stylesheet.
 *
 * @param {string} html
 * @returns {string}
 */
function htmlToText(html) {
  const spaced = String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n');

  const text = new DOMParser().parseFromString(spaced, 'text/html').body.textContent || '';

  return text.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * @param {object} picture Judge.me picture object: {original, small, compact, huge}
 * @returns {string|null} a safe https URL on a Judge.me host, or null
 */
function safeImageUrl(picture) {
  if (!picture) return null;

  // `small` is width=160, which suits both the 86px desktop thumb and the
  // 120px mobile one without shipping the 1024px original.
  const candidate = picture.small || picture.compact || picture.original;
  if (!candidate) return null;

  try {
    const url = new URL(candidate, location.href);
    if (url.protocol !== 'https:') return null;
    if (!IMAGE_HOSTS.test(url.hostname)) return null;
    return url.href;
  } catch {
    return null;
  }
}

function formatDate(iso) {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';

  return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(when);
}

class MonsterReviews extends HTMLElement {
  #page = 1;
  #summaryDone = false;

  connectedCallback() {
    this.load();

    const write = this.querySelector('[data-write-review]');
    if (write) write.addEventListener('click', () => this.#openWriteReview(write));
  }

  /**
   * A button that cannot do anything is worse than no button, so this decides
   * whether "Write a review" has a working destination and removes it if not.
   *
   * Judge.me's modal is created by their widget init — which currently throws
   * before it gets there, so on this store the modal does not exist and the
   * only route is the hosted form URL from the section setting. The check is
   * deliberately made at RUNTIME rather than baked in: the day Judge.me fixes
   * their bundle, the modal reappears and the button starts using it again
   * with no code change.
   */
  #settleWriteReview() {
    const write = this.querySelector('[data-write-review]');
    if (!write) return;

    const hasModal = !!document.querySelector('.jdgm-write-review-modal');
    if (hasModal || write.dataset.fallbackUrl) return;

    write.remove();
  }

  /**
   * Judge.me's WRITE-REVIEW MODAL still works even though their widget render
   * does not — the two are separate code paths, and the modal has been
   * confirmed opening and accepting a submission. So the button hands off to
   * it rather than reimplementing a review form, which would mean posting to
   * their API with our own credentials handling. Not worth owning.
   *
   * The handoff is a synthetic click on a link carrying THEIR class, because
   * their handler is delegated on that selector. That is a functional hook,
   * not a style reuse: the element is inert, has no dimensions and is removed
   * immediately. It is the one place jdgm-* appears outside the widget branch.
   *
   * If their modal never appears, we fall back to the hosted form URL from the
   * section setting — and if that is unset too, the button says so rather than
   * silently doing nothing.
   *
   * @param {HTMLElement} button
   */
  #openWriteReview(button) {
    const fallback = button.dataset.fallbackUrl;
    const modalBefore = document.querySelector('.jdgm-write-review-modal');

    if (modalBefore) {
      const proxy = document.createElement('a');
      proxy.className = 'jdgm-write-rev-link';
      proxy.href = '#';
      proxy.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
      document.body.append(proxy);
      proxy.click();
      proxy.remove();

      // Their open is not synchronous, so give it a beat before deciding it
      // failed. If the modal is on screen, we are done.
      setTimeout(() => {
        const modal = document.querySelector('.jdgm-write-review-modal');
        const open = modal && getComputedStyle(modal).display !== 'none';
        if (!open && fallback) window.location.href = fallback;
      }, 600);
      return;
    }

    if (fallback) window.location.href = fallback;
  }

  /** Exposed so the endpoint can be repointed and re-run when testing. */
  load() {
    this.#page = 1;
    this.#summaryDone = false;
    this.#fetchPage(1);
  }

  #url(page) {
    // Every parameter here was taken from the request the real widget sent and
    // then trimmed to what the endpoint actually requires. Dropping `platform`,
    // `url` or `shop_domain` returns 404; only the cache-busting `ts` was
    // droppable. Shop and product come from Liquid, never hardcoded.
    const params = new URLSearchParams({
      url: this.dataset.shopDomain,
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      product_id: this.dataset.productId,
      page: String(page),
      per_page: String(PER_PAGE),
    });

    return this.dataset.endpoint + '?' + params;
  }

  async #fetchPage(page) {
    try {
      const response = await fetch(this.#url(page), {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('HTTP ' + response.status);

      const data = await response.json();
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];

      // No reviews is not a failure, but it is still nothing to show. §5.7 is
      // explicit that an empty state must not ship an orphan heading, so the
      // section collapses whole rather than leaving a title over blank space.
      if (!reviews.length) return this.#collapse();

      this.#page = page;
      if (!this.#summaryDone) {
        this.#renderSummary(data);
        this.#summaryDone = true;
      }
      this.#renderRows(reviews);
      this.#renderPagination(data.pagination || {});
      // Late enough that Judge.me's own script has finished creating its modal
      // if it is going to.
      this.#settleWriteReview();
      this.dataset.state = 'ready';
    } catch (error) {
      // A dead endpoint must not leave a heading stranded over an empty band.
      console.warn('[monster-reviews] falling back to collapsed section:', error);
      this.#collapse();
    }
  }

  #collapse() {
    this.dataset.state = 'failed';
  }

  #stars(rating) {
    const wrap = document.createElement('span');
    wrap.className = 'monster-reviews__stars';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', rating + ' out of 5 stars');

    for (let i = 1; i <= 5; i += 1) {
      const star = document.createElement('span');
      star.setAttribute('aria-hidden', 'true');
      star.textContent = i <= Math.round(rating) ? '★' : '☆';
      if (i > Math.round(rating)) star.className = 'is-off';
      wrap.append(star);
    }

    return wrap;
  }

  #renderSummary(data) {
    const host = this.querySelector('[data-summary]');
    const average = Number(data.average_rating) || 0;
    const total = Number(data.number_of_reviews) || 0;

    host.querySelector('[data-average]').textContent = average.toFixed(1);
    host.querySelector('[data-stars]').replaceChildren(this.#stars(average));
    host.querySelector('[data-count]').textContent =
      'Based on ' + total + (total === 1 ? ' review' : ' reviews');

    // The histogram is supplied by the endpoint — five rows, each carrying
    // rating, frequency and percentage — so nothing here is computed or
    // inferred. If they ever stop sending it, the block hides rather than
    // showing numbers we made up.
    const histHost = host.querySelector('[data-histogram]');
    const rows = Array.isArray(data.histogram) ? data.histogram : [];
    if (!rows.length) {
      histHost.hidden = true;
    } else {
      const tpl = this.querySelector('[data-histogram-row]');
      histHost.replaceChildren(
        ...rows.map((row) => {
          const node = tpl.content.cloneNode(true);
          node.querySelector('[data-h-label]').textContent = row.rating + '★';
          node.querySelector('[data-h-fill]').style.width = (Number(row.percentage) || 0) + '%';
          node.querySelector('[data-h-percent]').textContent = (Number(row.percentage) || 0) + '%';
          node.querySelector('[data-h-count]').textContent = Number(row.frequency) || 0;
          return node;
        })
      );
    }

    host.hidden = false;
  }

  #renderRows(reviews) {
    const list = this.querySelector('[data-list]');
    const tpl = this.querySelector('[data-review-row]');

    list.replaceChildren(
      ...reviews.map((review) => {
        const node = tpl.content.cloneNode(true);
        const row = node.querySelector('.monster-reviews__row');

        row.querySelector('[data-r-stars]').replaceChildren(this.#stars(review.rating));

        // An empty title is real — the newest review has none — so the element
        // is removed rather than left as an empty line the grid still spaces.
        const title = row.querySelector('[data-r-title]');
        const titleText = String(review.title || '').trim();
        if (titleText) title.textContent = titleText;
        else title.remove();

        row.querySelector('[data-r-body]').textContent = htmlToText(review.body_html);
        row.querySelector('[data-r-name]').textContent = review.is_anonymous_reviewer
          ? 'Anonymous'
          : abbreviateName(review.reviewer_name);
        row.querySelector('[data-r-date]').textContent = formatDate(review.created_at);

        // Dormant by design: the imported reviews all carry verified_buyer
        // false, so this chip does not render until a genuinely verified
        // purchase review arrives.
        const chip = row.querySelector('[data-r-verified]');
        if (!review.verified_buyer) chip.remove();

        const figure = row.querySelector('[data-r-photo]');
        const picture = Array.isArray(review.pictures_urls) ? review.pictures_urls[0] : null;
        const src = safeImageUrl(picture);
        if (src) {
          const img = figure.querySelector('img');
          img.src = src;
          img.alt = '';
        } else {
          figure.remove();
        }

        return node;
      })
    );
  }

  #renderPagination(pagination) {
    const nav = this.querySelector('[data-paginate]');
    const total = Number(pagination.total_pages) || 1;

    if (total <= 1) {
      nav.hidden = true;
      nav.replaceChildren();
      return;
    }

    const current = Number(pagination.current_page) || this.#page;
    nav.replaceChildren(
      ...Array.from({ length: total }, (_, i) => {
        const page = i + 1;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'monster-reviews__page';
        button.textContent = String(page);
        button.setAttribute('aria-label', 'Page ' + page + ' of ' + total);
        if (page === current) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.addEventListener('click', () => this.#goTo(page, total));
        }
        return button;
      })
    );
    nav.hidden = false;
  }

  async #goTo(page, total) {
    await this.#fetchPage(page);

    // Announce before scrolling: a screen reader should hear what changed even
    // if the movement itself is instant.
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = 'Showing page ' + page + ' of ' + total + ' of reviews.';

    this.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
}

if (!customElements.get('monster-reviews')) {
  customElements.define('monster-reviews', MonsterReviews);
}
